import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles } from 'lucide-react'
import type { ReleaseOffer } from '../../pages/TodayFocus'
import { ORI_KIND_LABEL } from '../../utils/oriAsks'
import { AskEyebrow } from '../today/AskEyebrow'

type Outcome = 'accepted' | 'declined'

/**
 * Auto-Mom Release Policy offer surface.
 *
 * Renders the single already-approved "Want one more?" offer the orchestrator /
 * release gate decided is humane to surface after the active focus emptied.
 * Display-only: this card never decides whether to offer; it only renders the
 * approved result and records the user's accept/decline. It stays invisible
 * when there is no offer, so it is always safe to mount.
 *
 * "yanamas maximizes positive life momentum, not productivity."
 */
export function AutoMomReleaseOfferCard({
  releaseOffer,
  onResolved,
}: {
  releaseOffer?: ReleaseOffer | null
  /** Called after a successful accept/decline so the parent can refresh
   *  Today-view (an accepted offer pins the task and should surface now). */
  onResolved?: (outcome: Outcome) => void
}) {
  const navigate = useNavigate()
  const [resolved, setResolved] = useState<Outcome | null>(null)
  const [busy, setBusy] = useState(false)

  // Hide once resolved (either outcome) — the decision is recorded server-side.
  if (!releaseOffer || resolved) return null

  const handleOutcome = async (outcome: Outcome) => {
    if (busy) return
    setBusy(true)
    try {
      await fetch(
        `/api/orchestrator/release-offer/${releaseOffer.decision_id}/resolve`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ outcome }),
        },
      )
      setResolved(outcome)
      onResolved?.(outcome)
    } catch {
      // Fail soft: a resolve error must never crash Today view. Leave the card
      // up so the user can try again.
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-3xl border-[1px] border-brand-subtle-border bg-gradient-to-br from-brand-subtle to-surface p-4 shadow-sm">
      <AskEyebrow icon={<Sparkles />} label={ORI_KIND_LABEL.offer} typeWord="one more?" />

      <p
        className={`mt-2 text-lg font-bold text-primary ${releaseOffer.item_id ? 'cursor-pointer hover:underline' : ''}`}
        style={{ fontFamily: 'var(--font-display)' }}
        title={releaseOffer.item_id ? 'Open this item' : undefined}
        onClick={() => releaseOffer.item_id && navigate(`/tasks/${releaseOffer.item_id}`)}
      >
        {releaseOffer.title}
      </p>

      {releaseOffer.user_visible_reason && (
        <p className="mt-1 text-sm leading-relaxed text-secondary">
          {releaseOffer.user_visible_reason}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => handleOutcome('accepted')}
          className="min-h-touch rounded-xl bg-brand px-5 text-sm font-bold text-on-brand shadow-[0_4px_12px_rgba(120,81,169,0.3)] hover:bg-brand-hover disabled:opacity-60 transition-colors"
        >
          Yes, let's
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => handleOutcome('declined')}
          className="min-h-touch rounded-xl border border-brand-subtle-border bg-surface/70 px-4 text-sm text-secondary hover:bg-surface-alt disabled:opacity-60 transition-colors"
        >
          Not now
        </button>
      </div>
    </div>
  )
}
